import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading, EmptyState, StatusPill, Section8Badge } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Home, MapPin, ShieldCheck, CreditCard, Wrench, ArrowRight, CalendarDays, ClipboardCheck, ClipboardList } from "lucide-react";
import { RentNudgeBanner } from "@/components/RentNudgeBanner";
import { SmsRemindersCard } from "@/components/SmsRemindersCard";

function MoveOutPrompt({ mo }) {
  const navigate = useNavigate();
  if (!mo || mo.status !== "submitted") return null;
  return (
    <button onClick={() => navigate("/portal/move-out")} className="w-full rounded-2xl border border-emerald-200 bg-emerald-50 p-4 sm:p-5 mb-5 flex items-center gap-4 text-left card-hover" data-testid="move-out-prompt">
      <span className="h-11 w-11 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0"><ClipboardList className="h-5 w-5" /></span>
      <span className="flex-1 min-w-0">
        <span className="block font-display font-bold text-emerald-900">Your move-out statement is ready · {new Date(mo.submitted_at).toLocaleDateString()}</span>
        <span className="block text-sm text-emerald-800/80">See the move-in vs move-out comparison, deposit deductions and download the PDF.</span>
      </span>
      <ArrowRight className="h-5 w-5 text-emerald-600 shrink-0" />
    </button>
  );
}

function MoveInPrompt({ mi }) {
  const navigate = useNavigate();
  if (!mi || mi.status === "submitted") return null;
  const started = mi.status === "draft" && mi.rated > 0;
  return (
    <button onClick={() => navigate("/portal/move-in")} className="w-full rounded-2xl border border-indigo-200 bg-indigo-50 p-4 sm:p-5 mb-5 flex items-center gap-4 text-left card-hover" data-testid="move-in-prompt">
      <span className="h-11 w-11 rounded-xl bg-indigo-600 text-white flex items-center justify-center shrink-0"><ClipboardCheck className="h-5 w-5" /></span>
      <span className="flex-1 min-w-0">
        <span className="block font-display font-bold text-indigo-900">{started ? `Finish your move-in checklist · ${mi.rated}/${mi.total} rated` : "Complete your move-in checklist"}</span>
        <span className="block text-sm text-indigo-800/80">Document the condition of your home with photos — it protects your deposit at move-out.</span>
      </span>
      <ArrowRight className="h-5 w-5 text-indigo-600 shrink-0" />
    </button>
  );
}

export default function TenantDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/tenant/overview").then((r) => setData(r.data)); }, []);
  if (!data) return <Loading />;

  if (!data.lease)
    return (
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 mb-6">Hi {user?.name?.split(" ")[0]}</h1>
        <EmptyState icon={Home} title="No active lease yet" subtitle="Your landlord hasn't assigned you to a property yet. Check back soon." testid="tenant-no-lease" />
      </div>
    );

  const l = data.lease;
  const cur = data.current;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-7">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">Hi {user?.name?.split(" ")[0]} 👋</h1>
          <p className="text-sm text-slate-500 mt-1">Here's everything about your home and rent.</p>
        </div>
        <Button onClick={() => navigate("/portal/payments")} className="gap-2" data-testid="tenant-pay-cta"><CreditCard className="h-4 w-4" /> Pay rent</Button>
      </div>

      <RentNudgeBanner nudge={data.nudge} leaseId={l.id} />
      <MoveOutPrompt mo={data.move_out} />
      <MoveInPrompt mi={data.move_in} />

      {/* Home card */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden mb-5">
        <div className="grid md:grid-cols-5">
          <div className="md:col-span-2 h-48 md:h-auto bg-slate-100">
            <img src={l.property?.image_url} alt={l.property?.name} className="h-full w-full object-cover" />
          </div>
          <div className="md:col-span-3 p-6">
            <div className="flex items-center gap-2 mb-1">
              <h2 className="font-display font-bold text-xl text-slate-900">{l.property?.name}</h2>
              <Section8Badge section8={l.section8} />
            </div>
            <p className="text-sm text-slate-500 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{l.property?.address}, {l.property?.city} {l.property?.state}</p>
            <div className="grid grid-cols-3 gap-3 mt-5">
              <div className="rounded-xl border border-slate-200 p-3"><div className="text-[11px] font-semibold uppercase text-slate-500">Total rent</div><div className="text-lg font-bold font-display">{fmtMoney(l.total_rent)}</div></div>
              <div className="rounded-xl bg-amber-50 p-3"><div className="text-[11px] font-semibold uppercase text-amber-700">HAP covers</div><div className="text-lg font-bold font-display text-amber-900">{fmtMoney(l.hap_amount)}</div></div>
              <div className="rounded-xl bg-indigo-50 p-3"><div className="text-[11px] font-semibold uppercase text-indigo-700">You pay</div><div className="text-lg font-bold font-display text-indigo-900">{fmtMoney(l.tenant_portion)}</div></div>
            </div>
          </div>
        </div>
      </div>

      {/* Current month */}
      <div className="grid lg:grid-cols-3 gap-5 mb-5">
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-6">
          <div className="flex items-center gap-2 mb-4"><CalendarDays className="h-5 w-5 text-primary" /><h3 className="font-display font-bold text-lg text-slate-900">This month{cur ? ` · ${cur.label}` : ""}</h3></div>
          {cur ? (
            <div className="grid sm:grid-cols-3 gap-4">
              <div><div className="text-xs text-slate-500">Your copay due</div><div className="text-2xl font-extrabold font-display text-slate-900 mt-1">{fmtMoney(cur.tenant_due)}</div></div>
              <div><div className="text-xs text-slate-500">You've paid</div><div className="text-2xl font-extrabold font-display text-emerald-600 mt-1">{fmtMoney(cur.tenant_paid)}</div></div>
              <div><div className="text-xs text-slate-500">Balance</div><div className="text-2xl font-extrabold font-display text-rose-600 mt-1">{fmtMoney(cur.tenant_balance)}</div></div>
            </div>
          ) : <p className="text-sm text-slate-500">No charges this month.</p>}
          {cur?.fees > 0 && (
            <p className="text-xs text-rose-600 mt-3 font-medium" data-testid="tenant-dash-fee-note">Your balance includes {fmtMoney(cur.fees)} in late fees.</p>
          )}
          {cur && cur.tenant_balance > 0 && (
            <Button onClick={() => navigate("/portal/payments")} className="mt-5 gap-2" data-testid="tenant-pay-balance">Pay {fmtMoney(cur.tenant_balance)} <ArrowRight className="h-4 w-4" /></Button>
          )}
          {cur && cur.tenant_balance <= 0 && (
            <div className="mt-5 inline-flex items-center gap-2 rounded-xl bg-emerald-50 text-emerald-700 px-4 py-2.5 text-sm font-semibold"><ShieldCheck className="h-4 w-4" /> Your rent is fully paid this month!</div>
          )}
        </div>

        <button onClick={() => navigate("/portal/maintenance")} className="rounded-2xl border border-slate-200 bg-white p-6 text-left card-hover" data-testid="tenant-maint-cta">
          <span className="h-10 w-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center mb-3"><Wrench className="h-5 w-5" /></span>
          <h3 className="font-display font-bold text-slate-900">Maintenance</h3>
          <p className="text-sm text-slate-500 mt-1">{data.open_maintenance || 0} open request(s)</p>
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary mt-3">Submit a request <ArrowRight className="h-3.5 w-3.5" /></span>
        </button>
      </div>

      {/* Payment history */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 mb-5">
        <h3 className="font-display font-bold text-lg text-slate-900 mb-4">Payment history</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-slate-500 text-xs uppercase tracking-wider border-b border-slate-200">
              <tr><th className="text-left font-semibold py-2.5">Month</th><th className="text-right font-semibold py-2.5">You paid</th><th className="text-right font-semibold py-2.5">Your due</th><th className="text-right font-semibold py-2.5">Status</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.ledger.map((r) => (
                <tr key={r.period}>
                  <td className="py-3 font-medium text-slate-800">{r.label}</td>
                  <td className="py-3 text-right font-mono text-xs font-semibold">{fmtMoney(r.tenant_paid)}</td>
                  <td className="py-3 text-right font-mono text-xs text-slate-400">{fmtMoney(r.tenant_due)}</td>
                  <td className="py-3 text-right"><StatusPill status={r.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <SmsRemindersCard />
    </div>
  );
}
